import { useTenant } from '$features/auth/hooks/use-tenant'
import { TabLayout } from '$features/settings/layouts/tab'
import { Breadcrumb, Button, Card, DisplayLabel, Switch, Text, ToastHelper } from '$components'
import { cn } from '$utils'
import { CopyIcon, LinkIcon, SettingsIcon } from '$components'
import { SettingsRoutePath } from '$features/settings/routes/protected'
import { usePageTitle } from '$components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { tenantActiveQueryKey, tenantUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { Config } from '$config/app'
import { AppointmentRoutePath } from '$features/appointments/routes/protected'
import { Router } from '$utils/router'
import { Link } from 'react-router-dom'

export function OnlineBookingTab() {
  usePageTitle('Settings - Online Booking')
  const { tenantId, bookingUrl, isBookingEnabled } = useTenant()
  const queryClient = useQueryClient()

  const { mutate: updateTenant, isPending: isUpdating } = useMutation({
    ...tenantUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tenantActiveQueryKey(),
      })

      ToastHelper.success('Online booking settings updated.')
    },
    onError: () => ToastHelper.error('Failed to update online booking settings.'),
  })

  const handleToggleChange = (enabled: boolean) => {
    if (!tenantId) {
      return
    }

    updateTenant({
      path: { id: tenantId },
      body: {
        is_booking_enabled: enabled,
      },
    })
  }

  const fullBookingUrl = bookingUrl ? Config.getBookingPageUrl(bookingUrl) : null

  const handleViewBookingPage = () => {
    if (!fullBookingUrl) {
      return
    }
    window.open(fullBookingUrl, '_blank', 'noopener,noreferrer')
  }

  const handleCopyLink = async () => {
    if (!fullBookingUrl) {
      return
    }
    try {
      await navigator.clipboard.writeText(fullBookingUrl)
      ToastHelper.success('Link copied to clipboard')
    } catch {
      ToastHelper.error('Failed to copy link')
    }
  }

  return (
    <TabLayout>
      <Breadcrumb
        items={[
          {
            label: 'Settings',
            icon: SettingsIcon,
            path: SettingsRoutePath.Settings,
          },
          {
            label: 'Online Booking',
            path: SettingsRoutePath.SettingsOnlineBooking,
          },
        ]}
      />

      <div>
        <Text
          type="h2"
          weight="medium"
        >
          Online Booking
        </Text>

        <Text
          type="s"
          className="text-fg-subtle mt-stack-dense"
        >
          Allow customers to book appointments online via your public booking page.
        </Text>
      </div>

      <div className="space-y-stack-section">
        <div className="flex items-center gap-stack-section">
          <div className="flex-1">
            <DisplayLabel>Enable online booking</DisplayLabel>
            <Text
              type="s"
              className="text-fg-subtle"
            >
              When enabled, your clients can request appointments via the URL below. These requests appear on the{' '}
              <Link
                to={Router.getRoute(AppointmentRoutePath.AppointmentsRequests)}
                className="text-fg-link hover:text-fg-link underline"
              >
                appointment requests page
              </Link>
              {' '}and require your approval before they are confirmed.
            </Text>
          </div>

          <Switch
            id="is-booking-enabled"
            value={isBookingEnabled ?? false}
            onChange={handleToggleChange}
            disabled={isUpdating}
          />
        </div>

        <div>
          <DisplayLabel>Link to share with clients</DisplayLabel>
          <Text
            type="s"
            className={cn(
              'text-fg-subtle',
              !isBookingEnabled && 'text-fg-disabled',
            )}
          >
            Share this link with your clients so they can request appointments. You can add it to your social media, website, or anywhere else you connect with customers.
          </Text>
          <Card
            padding="sm"
            rounding="md"
            className={cn(
              'flex items-center gap-stack-roomy mt-stack',
              !isBookingEnabled && 'opacity-60',
            )}
          >
            <Text
              type="s"
              className={cn(
                'flex-1 min-w-0 truncate font-mono text-fg-muted',
                !isBookingEnabled && 'text-fg-disabled',
              )}
            >
              {fullBookingUrl ?? '—'}
            </Text>

            {bookingUrl && (
              <div className="flex gap-stack-tight shrink-0">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={CopyIcon}
                  onClick={handleCopyLink}
                >
                  Copy
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  icon={LinkIcon}
                  onClick={handleViewBookingPage}
                  disabled={!isBookingEnabled}
                >
                  View
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </TabLayout>
  )
}
